import { Link, useParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import { comicDetailImages, comicCoverImages } from '@/mocks/coverImages';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const category = comic ? categories.find((cat) => cat.id === comic.category) : undefined;
  const relatedComics = comic
    ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4)
    : [];

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">请检查链接是否正确</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${comic.title}-韩漫漫画,${comic.author}作品,免费在线漫画`}
        description={`${comic.title}是由${comic.author}创作的韩国漫画。${comic.description.substring(0, 120)}... 免费在线阅读，最新章节${comic.updatedChapter}。`}
        keywords={`${comic.title},韩漫,韩国漫画,${comic.author},${comic.tags.join(',')},免费漫画`}
        canonicalUrl={`/comic/${comic.slug}`}
        ogType="article"
        ogImage={comicDetailImages[comic.slug]}
        publishedTime={comic.updateDate}
        modifiedTime={comic.updateDate}
      />
      <SchemaOrg type="Article" data={{
        headline: `${comic.title} - ${comic.author}`,
        description: comic.description,
        author: comic.author,
        datePublished: comic.updateDate,
        dateModified: comic.updateDate,
        image: comicDetailImages[comic.slug],
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `${comic.title} - 韩漫漫画`,
        description: `${comic.title}漫画详情页`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://netkqn.com/' },
            { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://netkqn.com/category' },
            { '@type': 'ListItem', position: 3, name: `${category?.name || ''}漫画`, item: `http://netkqn.com/category/${comic.category}` },
            { '@type': 'ListItem', position: 4, name: comic.title, item: `http://netkqn.com/comic/${comic.slug}` },
          ],
        },
      }} />

      <Navbar />
      <main className="pt-20">
        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-5xl">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/category" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                {category && (
                  <>
                    <Link to={`/category/${category.slug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                      {category.name}漫画
                    </Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </>
                )}
                <span className="text-foreground-800 font-medium">{comic.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                <div className="flex-shrink-0 w-full md:w-72">
                  <div className="relative overflow-hidden rounded-2xl bg-background-100 aspect-[3/4]">
                    <img
                      src={comicDetailImages[comic.slug]}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      title={`${comic.title} - 韩国漫画`}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-foreground-500 text-sm mb-4">作者：{comic.author}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                      <i className="ri-star-fill text-xs"></i> {comic.rating}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm">
                      {comic.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                      {comic.chapters}话
                    </span>
                    <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                      {(comic.totalViews / 10000).toFixed(0)}万阅读
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">作品简介</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-full transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line"></i> 开始阅读
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium text-sm rounded-full transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bookmark-line"></i> 加入收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-5xl">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
                章节列表
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className="px-4 py-3 bg-background-50 hover:bg-primary-50 rounded-xl text-sm text-foreground-700 hover:text-primary-600 transition-colors text-left cursor-pointer"
                  >
                    第{comic.chapters - i}话
                  </button>
                ))}
                {comic.chapters > 24 && (
                  <div className="px-4 py-3 bg-background-50 rounded-xl text-sm text-foreground-500 text-center flex items-center justify-center">
                    查看更多...
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="mx-auto max-w-5xl">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/comic/${rc.slug}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-xl bg-background-100 aspect-[3/4] mb-2">
                        <img
                          src={comicCoverImages[rc.slug]}
                          alt={rc.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          title={rc.title}
                        />
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs">
                          {rc.rating}
                        </div>
                      </div>
                      <h4 className="text-foreground-950 text-sm font-medium truncate group-hover:text-primary-500 transition-colors">
                        {rc.title}
                      </h4>
                      <p className="text-foreground-500 text-xs mt-0.5">{rc.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}